import { ChangeDetectionStrategy, Component, HostBinding, Input, ViewChild } from '@angular/core';
import {
  APPROVE_DENY_REASON_VISIBILITY_LEVEL_LOOKUP,
  ApproveDenyReason,
  ApproveDenyReasonKeys,
  ApproveDenyReasonVisibilityLevel,
  ChangeSourceType,
  Constants,
  Lookup,
} from '@ag-common-lib/public-api';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AgentApproveDenyReasonsService } from 'ag-common-svc/lib/services/agent-approve-deny-reason.service';
import { AgentService, AuthService } from 'ag-common-svc/public-api';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { ApproveDenyReasonsModalComponent } from '../approve-deny-reasons-modal/approve-deny-reasons-modal.component';
import { ApproveDenyReasonEditorConfig } from './approve-deny-reasons-grid.model';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';

type ApproveDenyReasonsGridState = {
  dataSource: DataSource;
  createdByLookup: Partial<Lookup>[];
};

@UntilDestroy()
@Component({
  selector: 'ag-shr-approve-deny-reasons-grid',
  templateUrl: './approve-deny-reasons-grid.component.html',
  styleUrls: ['./approve-deny-reasons-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApproveDenyReasonsGridComponent {
  @HostBinding('class') className = 'approve-deny-reasons-grid';
  @ViewChild('approveDenyReasonModalRef')
  approveDenyReasonsModalComponent: ApproveDenyReasonsModalComponent;
  @Input() set agentDbId(dbId: string) {
    this.agentDbId$.next(dbId);
    this.agentDbIdValue = dbId ?? null;
  }
  @Input() title: string;
  @Input() isEditable: boolean = true;
  @Input() canEdit: boolean = true;
  @Input() canDelete: boolean = true;
  @Input() canCreate: boolean = true;
  @Input() sourceType: ChangeSourceType | null = null;
  @Input() extraToolbarItems = [];
  @Input() editModalOptions: ApproveDenyReasonEditorConfig;
  @Input() set allowedVisibilityLevels(data: ApproveDenyReasonVisibilityLevel[]) {
    if (Array.isArray(data)) {
      this.allowedVisibilityLevels$.next(data);
    }
  }

  public BaseModelKeys = BaseModelKeys;
  public ApproveDenyReasonKeys = ApproveDenyReasonKeys;
  public approveDenyReasonVisibilityLevelLookup = APPROVE_DENY_REASON_VISIBILITY_LEVEL_LOOKUP;
  public gridState$: Observable<ApproveDenyReasonsGridState>;

  private loggedInUserEmail: string;
  private loggedInAgentDbId: string;
  private readonly agentDbId$ = new BehaviorSubject<string>(undefined);
  public agentDbIdValue: string | null = null;
  private allowedVisibilityLevels$ = new BehaviorSubject<ApproveDenyReasonVisibilityLevel[]>([]);
  readonly dateFormat: string = Constants.DISPLAY_DATE_FORMAT;

  constructor(
    private agentService: AgentService,
    private agentApproveDenyReasonsService: AgentApproveDenyReasonsService,
    private authService: AuthService,
  ) {
    this.authService.currentUser$
      ?.pipe(
        untilDestroyed(this),
        map(user => user?.email),
      )
      .subscribe(loggedInUserEmail => {
        this.loggedInUserEmail = loggedInUserEmail;
      });
    this.authService.loggedInAgent$
      ?.pipe(
        untilDestroyed(this),
        map(agent => agent?.[BaseModelKeys.dbId]),
      )
      .subscribe(loggedInAgentDbId => {
        this.loggedInAgentDbId = loggedInAgentDbId;
      });

    this.gridState$ = combineLatest({
      agentDbId: this.agentDbId$,
      allowedVisibilityLevels: this.allowedVisibilityLevels$,
    }).pipe(
      filter(({ agentDbId }) => !!agentDbId),
      switchMap(({ agentDbId, allowedVisibilityLevels }) => {
        const qp: QueryParam[] = [];

        if (Array.isArray(allowedVisibilityLevels) && allowedVisibilityLevels?.length) {
          const visibilityLevelsQueryParam = new QueryParam(
            ApproveDenyReasonKeys.visibilityLevel,
            WhereFilterOperandKeys.in,
            allowedVisibilityLevels,
          );
          qp.push(visibilityLevelsQueryParam);
        }

        return this.agentApproveDenyReasonsService.getList(agentDbId, qp);
      }),
      switchMap(approveDenyReasons => {
        const rows = Array.isArray(approveDenyReasons) ? approveDenyReasons : [];
        const createdByAgentIds = rows
          .map(row => row?.[BaseModelKeys.createdByAgentDbId])
          .filter((id): id is string => !!id);

        return from(this.agentService.getAgentsLookupByIds(createdByAgentIds)).pipe(
          map(createdByLookup => ({
            dataSource: new DataSource({
              store: new ArrayStore({
                key: 'dbId',
                data: rows,
              }),
            }),
            createdByLookup,
          })),
        );
      }),
      shareReplay(1),
    );
  }

  public onRowRemoving = e => {
    e.cancel = this.agentApproveDenyReasonsService.delete(
      this.agentDbId$.value,
      e.data[BaseModelKeys.dbId],
      this.sourceType,
    );
  };

  public showAddApproveDenyReasonPopup = () => {
    this.approveDenyReasonsModalComponent.showModal(
      this.agentDbId$.value,
      this.editModalOptions?.initialApproveDenyReason as ApproveDenyReason,
    );
  };

  public showEditPopup = ({ row: { data } }) => {
    this.approveDenyReasonsModalComponent.showModal(this.agentDbId$.value, data);
  };

  public getIsEditingAllowed = ({ row: { data } }) => {
    if (data.isSystem) {
      return false;
    }

    if (this.loggedInUserEmail && this.loggedInUserEmail === data[BaseModelKeys.createdByAgentDbId]) {
      return true;
    }

    if (!!this.loggedInUserEmail && this.loggedInUserEmail === data[BaseModelKeys.createdByEmail]) {
      return true;
    }

    return false;
  };

}
